"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OfficesHttpService = void 0;
const types_1 = require("../../types");
const success_response_schema_1 = require("../../types/global/success-response.schema");
const pending_invitation_for_office_1 = require("../../types/invitations/pending-invitation-for-office");
const assign_office_csm_1 = require("../../types/offices/assign-office-csm");
const create_office_1 = require("../../types/offices/create-office");
const delete_office_1 = require("../../types/offices/delete-office");
const doctolib_planning_sync_1 = require("../../types/offices/doctolib-planning-sync");
const get_office_1 = require("../../types/offices/get-office");
const get_offices_agendas_list_1 = require("../../types/offices/get-offices-agendas-list");
const get_offices_list_1 = require("../../types/offices/get-offices-list");
const get_offices_stats_1 = require("../../types/offices/get-offices-stats");
const send_available_slots_reminder_1 = require("../../types/offices/send-available-slots-reminder");
const update_office_1 = require("../../types/offices/update-office");
const update_office_site_1 = require("../../types/offices/update-office-site");
const http_service_1 = require("../http.service");
class OfficesHttpService extends http_service_1.HttpService {
    constructor(apiUrl) {
        super(`${apiUrl}/offices`);
    }
    getList(payload) {
        const params = {
            page: String(payload.page),
            per_page: String(payload.per_page),
            timezone: payload.timezone,
        };
        if (payload.search) {
            params.search = payload.search;
        }
        if (payload.includeDeactivated) {
            params.includeDeactivated = 'true';
        }
        return this.getEntityList('', {
            params,
            schema: get_offices_list_1.getOfficesListApiSchema,
        });
    }
    getAgendasList(payload) {
        const params = {};
        if (payload.doctorSubstituteId) {
            params.doctorSubstituteId = payload.doctorSubstituteId;
        }
        return this.getEntityList('agendas', {
            params,
            schema: get_offices_agendas_list_1.getOfficesAgendasListApiSchema,
        });
    }
    getStats(payload) {
        const params = {
            from: payload.from.toISOString(),
            to: payload.to.toISOString(),
            timezone: payload.timezone,
        };
        return this.getEntityList('stats', {
            params,
            schema: get_offices_stats_1.getOfficesStatsApiSchema,
        });
    }
    getActivity(payload) {
        return this.getEntityList('activity', {
            params: payload,
            schema: types_1.getOfficesActivityApiSchema,
        });
    }
    createPlanning(officeId, payload) {
        const body = {
            from: payload.from,
            to: payload.to,
            timezone: payload.timezone,
        };
        const url = this.getApiUrlOf(`${officeId}/planning`);
        return this.fetch(url, {
            method: 'POST',
            body,
            transformTo: 'blob',
        });
    }
    triggerDoctolibSync(payload) {
        const body = {
            from: payload.from,
            to: payload.to,
        };
        const officeId = encodeURIComponent(payload.officeId);
        return this.post(`${officeId}/doctolib-planning-sync`, {
            body,
            schema: doctolib_planning_sync_1.triggerDoctolibPlanningSyncApiResponseSchema,
        });
    }
    triggerDoctolibReset(payload) {
        const body = {
            from: payload.from,
            to: payload.to,
        };
        const officeId = encodeURIComponent(payload.officeId);
        return this.post(`${officeId}/doctolib-planning-reset`, {
            body,
            schema: doctolib_planning_sync_1.triggerDoctolibPlanningSyncApiResponseSchema,
        });
    }
    getDoctolibSyncRun(runId) {
        const encodedRunId = encodeURIComponent(runId);
        return this.get(`doctolib-planning-sync/runs/${encodedRunId}`, {
            schema: doctolib_planning_sync_1.doctolibPlanningSyncRunApiResponseSchema,
        });
    }
    getPlanning(officeId, payload) {
        const params = {
            from: payload.from,
            to: payload.to,
            timezone: payload.timezone,
        };
        return this.getEntityList(`${officeId}/planning`, {
            params,
            schema: types_1.getOfficePlanningApiSchema,
        });
    }
    getDetails(id) {
        return this.getEntity(id, {
            schema: get_office_1.getOfficeApiResponseSchema,
        });
    }
    create(payload) {
        return this.postEntity('', {
            body: payload,
            schema: create_office_1.createOfficeApiResponseSchema,
        });
    }
    update(id, payload) {
        return this.putEntity(id, {
            body: payload,
            schema: update_office_1.updateOfficeApiResponseSchema,
        });
    }
    updateSite(id, payload) {
        return this.putEntity(`${id}/site`, {
            body: payload,
            schema: update_office_site_1.updateOfficeSiteApiResponseSchema,
        });
    }
    assignCsm(id, payload) {
        return this.putEntity(`${id}/csm`, {
            body: payload,
            schema: assign_office_csm_1.assignOfficeCsmApiResponseSchema,
        });
    }
    /**
     * Closes a practice: its history stays readable, writes are refused and it
     * leaves the substitute-facing lists.
     */
    deactivate(id) {
        return this.post(`${encodeURIComponent(id)}/deactivate`, { schema: success_response_schema_1.successResponseSchema });
    }
    /** Reverts {@link deactivate}. */
    reactivate(id) {
        return this.post(`${encodeURIComponent(id)}/reactivate`, { schema: success_response_schema_1.successResponseSchema });
    }
    deleteById(id) {
        return this.delete(id, {
            schema: delete_office_1.deleteOfficeApiResponseSchema,
        });
    }
    sendAvailableSlotsReminder(payload) {
        return this.post('send-available-slots-reminder', {
            body: payload,
            schema: send_available_slots_reminder_1.sendAvailableSlotsReminderOfficeApiResponseSchema,
        });
    }
    generateOccupationReport(payload) {
        const params = {
            from: payload.from,
            to: payload.to,
        };
        return this.get(`${payload.officeId}/occupation-report`, {
            params,
            schema: types_1.generateOccupationReportApiResponseSchema,
        });
    }
    sendOccupationReportReminder(payload) {
        const body = {
            from: payload.from,
            to: payload.to,
        };
        return this.post(`${payload.officeId}/send-occupation-report-reminder`, {
            body,
            schema: types_1.sendOccupationReportReminderApiResponseSchema,
        });
    }
    processSkelloReconciliation(officeId, formData) {
        return this.post(`${officeId}/skello-reconciliation`, {
            body: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }
    getSkelloReconciliation(officeId, params) {
        return this.get(`${officeId}/skello-reconciliation`, {
            params,
        });
    }
    getSkelloReconciliationHistory(officeId, params) {
        const queryParams = params?.limit
            ? { limit: params.limit.toString() }
            : undefined;
        return this.get(`${officeId}/skello-reconciliation/reports`, {
            params: queryParams,
        });
    }
    resetSkelloReconciliation(officeId, params) {
        return this.delete(`${officeId}/skello-reconciliation`, {
            body: params,
        });
    }
    getPendingInvitations(officeId) {
        return this.get(`${officeId}/pending-invitations`, {
            schema: pending_invitation_for_office_1.getPendingInvitationsForOfficeResponseSchema,
        });
    }
}
exports.OfficesHttpService = OfficesHttpService;
